///////////////////////////////////////////////////////////////////////////////////////////////////
/// @file   WorldMap.tsx
/// @brief  SVG world map of agent sites with license-move (red, old site -> new site) and
///         same-account arcs. Pan + wheel zoom.
///
/// @copyright Wheatstone Corporation 2026, All rights reserved.
///////////////////////////////////////////////////////////////////////////////////////////////////

import { memo, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { geoGraticule10, geoNaturalEarth1, geoPath } from 'd3-geo';
import { select } from 'd3-selection';
import 'd3-transition';
import { zoom, zoomIdentity, type ZoomBehavior, type ZoomTransform } from 'd3-zoom';
import { feature } from 'topojson-client';
import type { Topology, GeometryCollection } from 'topojson-specification';
import landTopo from 'world-atlas/land-110m.json';
import type { MapLink, MapSite } from './types';

const W = 960;
const H = 500;

const projection = geoNaturalEarth1().fitExtent([[12, 12], [W - 12, H - 12]], { type: 'Sphere' });
const path = geoPath(projection);
const topo = landTopo as unknown as Topology<{ land: GeometryCollection }>;
const LAND_D = path(feature(topo, topo.objects.land)) ?? '';
const GRATICULE_D = path(geoGraticule10()) ?? '';
const SPHERE_D = path({ type: 'Sphere' }) ?? '';

export interface PlacedSite extends MapSite {
  x: number;
  y: number;
}

interface Props {
  sites: MapSite[];
  links: MapLink[];
  showAccountLinks: boolean;
  selectedIp: string | null;
  selectedLinkKey: string | null;
  onSelectSite: (ip: string | null) => void;
  onSelectLink: (key: string | null) => void;
}

export function linkKey(l: MapLink): string {
  return `${l.a}|${l.b}|${l.customer_id}|${l.kind}`;
}

/// Arched path between two projected points: a quadratic curve bowed
/// "upward" so overlapping great-circle routes read as distinct flight paths.
function arcPath(a: PlacedSite, b: PlacedSite, bend: number): string {
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dist = Math.hypot(dx, dy) || 1;
  let nx = -dy / dist;
  let ny = dx / dist;
  if (ny > 0) { nx = -nx; ny = -ny; }
  const lift = Math.min(160, dist * 0.28) * bend;
  return `M${a.x},${a.y} Q${mx + nx * lift},${my + ny * lift} ${b.x},${b.y}`;
}

const KIND_ORDER: Record<MapLink['kind'], number> = { same_account: 0, refused: 1, license_move: 2 };

function nodeRadius(site: MapSite): number {
  return 3.2 + Math.sqrt(Math.max(1, site.agents.length)) * 1.6;
}

/// Enter/Space activates an SVG element given role="button".
function onActivateKey(fn: () => void) {
  return (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      fn();
    }
  };
}

function placeLabel(site: MapSite): string {
  const g = site.geo;
  return [g.city, g.country].filter(Boolean).join(', ') || site.ip;
}

function WorldMap({
  sites, links, showAccountLinks, selectedIp, selectedLinkKey, onSelectSite, onSelectLink,
}: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const zoomRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const [t, setT] = useState<ZoomTransform>(zoomIdentity);
  const [hover, setHover] = useState<{ site: PlacedSite; x: number; y: number } | null>(null);

  useEffect(() => {
    if (!svgRef.current) return;
    const z = zoom<SVGSVGElement, unknown>()
      .scaleExtent([1, 10])
      .translateExtent([[0, 0], [W, H]])
      .on('zoom', (e) => setT(e.transform));
    zoomRef.current = z;
    select(svgRef.current).call(z).on('dblclick.zoom', null);
    return () => { select(svgRef.current!).on('.zoom', null); };
  }, []);

  // Sites that geolocate to the same spot (several NAT addresses of one
  // office, cloud regions) are fanned out on a small ring so each stays
  // clickable and a license-move path between them is still drawn.
  const placed = useMemo(() => {
    const groups = new Map<string, { site: MapSite; x: number; y: number }[]>();
    for (const s of sites) {
      if (s.geo.lat === null || s.geo.lon === null) continue;
      const p = projection([s.geo.lon, s.geo.lat]);
      if (!p) continue;
      const key = `${Math.round(p[0])},${Math.round(p[1])}`;
      const g = groups.get(key) ?? [];
      g.push({ site: s, x: p[0], y: p[1] });
      groups.set(key, g);
    }
    const out = new Map<string, PlacedSite>();
    for (const g of groups.values()) {
      g.sort((a, b) => (a.site.ip < b.site.ip ? -1 : 1));
      const ring = g.length > 1 ? 4 + g.length * 0.8 : 0;
      g.forEach(({ site, x, y }, i) => {
        const angle = (2 * Math.PI * i) / g.length - Math.PI / 2;
        out.set(site.ip, { ...site, x: x + ring * Math.cos(angle), y: y + ring * Math.sin(angle) });
      });
    }
    return out;
  }, [sites]);

  // Several links can join the same two sites (one per customer): fan them out.
  const drawn = useMemo(() => {
    const seen = new Map<string, number>();
    return links
      .filter((l) => l.kind !== 'same_account' || showAccountLinks)
      .map((l) => {
        // A move is drawn from the old site to the new one (latest move), so
        // the arrowhead and the marching dashes point where the license went.
        // A refusal is drawn from the site holding the seats to the refused one.
        const fromIp = l.kind === 'license_move' && l.moves[0] ? l.moves[0].from_ip
          : l.kind === 'refused' && l.refusals[0] ? (l.refusals[0].ip === l.a ? l.b : l.a)
          : l.a;
        const toIp = fromIp === l.a ? l.b : l.a;
        const a = placed.get(fromIp);
        const b = placed.get(toIp);
        if (!a || !b || (a.x === b.x && a.y === b.y)) return null;
        const pairKey = `${l.a}|${l.b}`;
        const n = seen.get(pairKey) ?? 0;
        seen.set(pairKey, n + 1);
        return { link: l, key: linkKey(l), d: arcPath(a, b, 1 + n * 0.35) };
      })
      .filter((x): x is { link: MapLink; key: string; d: string } => x !== null)
      // informational first, then yellow, then red on top
      .sort((x, y) => KIND_ORDER[x.link.kind] - KIND_ORDER[y.link.kind]);
  }, [links, placed, showAccountLinks]);

  const focusIps = useMemo(() => {
    if (selectedLinkKey) {
      const l = links.find((x) => linkKey(x) === selectedLinkKey);
      return l ? new Set([l.a, l.b]) : null;
    }
    return null;
  }, [links, selectedLinkKey]);

  const k = t.k;
  const resetZoom = () => {
    if (svgRef.current && zoomRef.current) {
      select(svgRef.current).transition().duration(450).call(zoomRef.current.transform, zoomIdentity);
    }
  };

  const nodes = [...placed.values()].sort((a, b) => b.agents.length - a.agents.length);

  return (
    <div className="fm-map" onMouseLeave={() => setHover(null)}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        className="fm-svg"
        role="group"
        aria-label="World map of license server agents"
        onClick={(e) => {
          if (e.target === svgRef.current || (e.target as Element).classList.contains('fm-bg')) {
            onSelectSite(null);
            onSelectLink(null);
          }
        }}
      >
        <defs>
          {/* userSpaceOnUse keeps the arrowhead a fixed size as the path scales with zoom;
              refX past the tip parks it just short of the destination node. */}
          <marker id="fm-arrow" viewBox="0 0 10 10" refX="19" refY="5" markerWidth={9 / k} markerHeight={9 / k}
            markerUnits="userSpaceOnUse" orient="auto">
            <path d="M0,1 L10,5 L0,9 Z" className="fm-arrowhead" />
          </marker>
          <marker id="fm-arrow-y" viewBox="0 0 10 10" refX="19" refY="5" markerWidth={9 / k} markerHeight={9 / k}
            markerUnits="userSpaceOnUse" orient="auto">
            <path d="M0,1 L10,5 L0,9 Z" className="fm-arrowhead-y" />
          </marker>
          <pattern id="fm-dots" width="4" height="4" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="0.9" className="fm-dot" />
          </pattern>
          <radialGradient id="fm-vignette" cx="50%" cy="50%" r="65%">
            <stop offset="60%" stopColor="#000" stopOpacity="0" />
            <stop offset="100%" stopColor="#000" stopOpacity="0.65" />
          </radialGradient>
        </defs>

        <rect className="fm-bg" x="0" y="0" width={W} height={H} />

        <g transform={t.toString()}>
          <path d={SPHERE_D} className="fm-sphere" />
          <path d={GRATICULE_D} className="fm-graticule" strokeWidth={0.5 / k} />
          <path d={LAND_D} className="fm-land" fill="url(#fm-dots)" />
          <path d={LAND_D} className="fm-coast" strokeWidth={0.7 / k} />

          {drawn.map(({ link, key, d }) => {
            const moved = link.kind === 'license_move';
            const refused = link.kind === 'refused';
            const alert = moved || refused;
            const dim = (focusIps && key !== selectedLinkKey) ||
              (selectedIp && link.a !== selectedIp && link.b !== selectedIp);
            const active = key === selectedLinkKey;
            return (
              <g key={key} className={`fm-link ${moved ? 'fm-link-move' : refused ? 'fm-link-refused' : 'fm-link-account'} ${dim ? 'fm-dim' : ''} ${active ? 'fm-active' : ''}`}>
                {/* Glow = static wide translucent strokes, not a blur filter: a
                    filter under an animated dash re-blurs every frame. */}
                {alert && <path d={d} className="fm-link-halo" strokeWidth={7 / k} />}
                {alert && <path d={d} className="fm-link-halo fm-link-halo-inner" strokeWidth={3.5 / k} />}
                <path d={d} className="fm-link-line" strokeWidth={(alert ? 1.6 : 1.1) / k}
                  strokeDasharray={alert ? `${6 / k} ${5 / k}` : `${2 / k} ${4 / k}`}
                  markerEnd={moved ? 'url(#fm-arrow)' : refused ? 'url(#fm-arrow-y)' : undefined} />
                <path d={d} className="fm-link-hit" strokeWidth={10 / k}
                  role="button"
                  tabIndex={0}
                  aria-pressed={active}
                  aria-label={`${moved ? 'License moved' : refused ? 'Refused, seats held elsewhere' : 'Same account'} path for ${link.customer_username}: ${link.a} to ${link.b}`}
                  onClick={(e) => { e.stopPropagation(); onSelectSite(null); onSelectLink(active ? null : key); }}
                  onKeyDown={onActivateKey(() => { onSelectSite(null); onSelectLink(active ? null : key); })}>
                  <title>{`${link.customer_username}: ${link.a} ↔ ${link.b}`}</title>
                </path>
              </g>
            );
          })}

          {nodes.map((s) => {
            const r = nodeRadius(s) / Math.sqrt(k);
            const selected = s.ip === selectedIp;
            // Neighbours via VISIBLE paths only: a hidden account link must
            // not reveal a relationship the map is not drawing.
            const dim = (focusIps && !focusIps.has(s.ip)) || (selectedIp && !selected && !drawn.some(
              ({ link: l }) => (l.a === selectedIp && l.b === s.ip) || (l.b === selectedIp && l.a === s.ip)));
            return (
              <g
                key={s.ip}
                className={`fm-node fm-${s.status} ${s.moved ? 'fm-node-moved' : ''} ${s.refused ? 'fm-node-refused' : ''} ${selected ? 'fm-selected' : ''} ${dim ? 'fm-dim' : ''}`}
                transform={`translate(${s.x},${s.y})`}
                role="button"
                tabIndex={0}
                aria-pressed={selected}
                aria-label={`${placeLabel(s)}, ${s.agents.length} agent${s.agents.length === 1 ? '' : 's'}, ${s.status}${s.moved ? ', license moved' : ''}${s.refused ? ', refused seats held elsewhere' : ''}`}
                onKeyDown={onActivateKey(() => { onSelectLink(null); onSelectSite(selected ? null : s.ip); })}
                onMouseMove={(e) => {
                  const box = svgRef.current?.getBoundingClientRect();
                  if (box) setHover({ site: s, x: e.clientX - box.left, y: e.clientY - box.top });
                }}
                onMouseLeave={() => setHover(null)}
                onClick={(e) => { e.stopPropagation(); onSelectLink(null); onSelectSite(selected ? null : s.ip); }}
                data-testid={`map-site-${s.ip}`}
              >
                {/* Pulse only where it means something (license moved): every
                    animated SVG element repaints the map each frame. */}
                {s.moved && s.status === 'online' && <circle r={r} className="fm-ping" />}
                {s.moved && <circle r={r + 3.5 / Math.sqrt(k)} className="fm-threat-ring" strokeWidth={1 / k} />}
                {s.refused && <circle r={r + (s.moved ? 6 : 3.5) / Math.sqrt(k)} className="fm-refused-ring" strokeWidth={1 / k} />}
                {selected && (
                  <g className="fm-reticle" strokeWidth={1 / k}>
                    <circle r={r + 7 / Math.sqrt(k)} />
                    <path d={`M${-(r + 11 / Math.sqrt(k))},0 h${5 / Math.sqrt(k)} M${r + 11 / Math.sqrt(k)},0 h${-5 / Math.sqrt(k)} M0,${-(r + 11 / Math.sqrt(k))} v${5 / Math.sqrt(k)} M0,${r + 11 / Math.sqrt(k)} v${-5 / Math.sqrt(k)}`} />
                  </g>
                )}
                <circle r={r * 1.9} className="fm-aura" />
                <circle r={r} className="fm-core" />
                <circle r={Math.max(1, r * 0.35)} className="fm-center" />
                {(k >= 2.5 || selected) && (
                  <text className="fm-label" x={r + 4 / k} y={-r - 2 / k} fontSize={9 / k}>
                    {placeLabel(s)}
                  </text>
                )}
              </g>
            );
          })}
        </g>

        <rect x="0" y="0" width={W} height={H} fill="url(#fm-vignette)" pointerEvents="none" />
      </svg>

      <div className="fm-scanlines" aria-hidden />
      <span className="fm-corner fm-tl" aria-hidden />
      <span className="fm-corner fm-tr" aria-hidden />
      <span className="fm-corner fm-bl" aria-hidden />
      <span className="fm-corner fm-br" aria-hidden />

      <div className="fm-legend">
        <div><i className="fm-sw fm-sw-online" />ONLINE &lt;15M</div>
        <div><i className="fm-sw fm-sw-idle" />IDLE &lt;1H</div>
        <div><i className="fm-sw fm-sw-offline" />OFFLINE</div>
        <div><i className="fm-sw-line fm-sw-move" />LICENSE MOVED</div>
        <div><i className="fm-sw-line fm-sw-refused" />REFUSED · SEATS HELD ELSEWHERE</div>
        {showAccountLinks && <div><i className="fm-sw-line fm-sw-account" />SAME ACCOUNT</div>}
        {/* DB-IP Lite is CC BY 4.0: attribution required wherever its data is shown. */}
        <a className="fm-attrib" href="https://db-ip.com" target="_blank" rel="noopener noreferrer">IP Geolocation by DB-IP</a>
      </div>

      <div className="fm-zoom">
        <button type="button" onClick={() => svgRef.current && zoomRef.current && select(svgRef.current).transition().duration(250).call(zoomRef.current.scaleBy, 1.6)} aria-label="Zoom in">+</button>
        <button type="button" onClick={() => svgRef.current && zoomRef.current && select(svgRef.current).transition().duration(250).call(zoomRef.current.scaleBy, 1 / 1.6)} aria-label="Zoom out">−</button>
        <button type="button" onClick={resetZoom} aria-label="Reset zoom">⌂</button>
        <span>{k.toFixed(1)}×</span>
      </div>

      {hover && (
        <div
          className="fm-tooltip"
          style={{ left: Math.min(hover.x + 14, (svgRef.current?.clientWidth ?? 600) - 230), top: hover.y + 14 }}
        >
          <div className="fm-tt-title">{placeLabel(hover.site)}</div>
          <div className="fm-tt-row"><span>IP</span><b>{hover.site.ip}</b></div>
          {hover.site.geo.org && <div className="fm-tt-row"><span>NET</span><b>{hover.site.geo.org}</b></div>}
          <div className="fm-tt-row"><span>AGENTS</span><b>{hover.site.agents.length}</b></div>
          <div className="fm-tt-row"><span>ACCOUNTS</span><b>{hover.site.customers.map((c) => c.username).join(', ') || '—'}</b></div>
          <div className="fm-tt-row"><span>STATUS</span><b className={`fm-txt-${hover.site.status}`}>{hover.site.status.toUpperCase()}</b></div>
          {hover.site.moved && <div className="fm-tt-alert">⚠ A LICENSE MOVED TO / FROM THIS SITE</div>}
          {hover.site.refused && <div className="fm-tt-warn">⚠ REFUSED SEATS HELD AT ANOTHER SITE</div>}
        </div>
      )}
    </div>
  );
}

// Re-renders only when its props change (the page's clock ticks separately).
export default memo(WorldMap);
